import { HSLColor, RGBAColor, RGBColor } from "../types";
import { isArray, isNumber, pad2 } from "../utils/utils";

/**
 * Source: http://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
 *
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   number  h       The hue
 * @param   number  s       The saturation
 * @param   number  l       The lightness
 * @return  Array           The RGB representation
 */
function hslToRgb(h: number, s: number, l: number): RGBColor {
    var r: number, g: number, b: number;

    if (s == 0) {
        r = g = b = l; // achromatic
    } else {
        var hue2rgb = function hue2rgb(p: number, q: number, t: number) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
/**
 * Source: http://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
 *
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   number  r       The red color value
 * @param   number  g       The green color value
 * @param   number  b       The blue color value
 * @return  Array           The HSL representation
 */
export function rgbToHsl(r: number, g: number, b: number): HSLColor {
    (r /= 255), (g /= 255), (b /= 255);
    var max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    var h,
        s,
        l = (max + min) / 2;

    if (max == min) {
        h = s = 0; // achromatic
    } else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    return [h, s, l];
}

export function rgbToHslArray(rgb: RGBColor): HSLColor {
    const hsl = rgbToHsl(rgb[0], rgb[1], rgb[2]);
    return [hsl[0], hsl[1], hsl[2]];
}

export function rgbToHex(rgb: RGBColor): string {
    return rgb.map((v) => pad2(v.toString(16))).join("");
}

export function hexToRgbNumber(hex: string): number {
    hex = hex.replace("#", "");
    let num = parseInt(hex, 16);
    return num;
}

export function hexToRgbArray(hex: string) {
    let num = hexToRgbNumber(hex);
    return rgbNumToArray(num);
}

export function rgbNumToArray(num: number): RGBColor {
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

export function rgbNumFromArray(rgb: RGBColor) {
    return ((rgb[0] & 255) << 16) | ((rgb[1] & 255) << 8) | (rgb[2] & 255);
}

export function createLinearGradient(colors: RGBColor[], count: number): RGBColor[] {
    if (!(colors && colors.length >= 2)) throw new Error("colors: must include at least 2 colors");

    //var hslColors = colors.map(c => rgbToHsl(c[0], c[1], c[2]));

    var step = (colors.length - 1) / count;
    var p = 0;

    var results: RGBColor[] = [colors[0]];

    for (var i = 1; i < count - 1; i++) {
        p += step;

        var pe = Math.floor(p);

        if (p == pe) {
            results.push(colors[pe]);
        } else {
            var delta = p - pe;

            //var c1 = hslColors[pe];
            //var c2 = hslColors[pe + 1];
            //var h = c1[0] + ((c2[0] - c1[0]) * delta);
            //var s = c1[1] + ((c2[1] - c1[1]) * delta);
            //var l = c1[2] + ((c2[2] - c1[2]) * delta);
            //let rgb = hslToRgb(h, s, l);

            var c1 = colors[pe];
            var c2 = colors[pe + 1];
            var r = Math.round(c1[0] + (c2[0] - c1[0]) * delta);
            var g = Math.round(c1[1] + (c2[1] - c1[1]) * delta);
            var b = Math.round(c1[2] + (c2[2] - c1[2]) * delta);
            let rgb = [r, g, b];

            results.push(rgb);
        }
    }

    results.push(colors[colors.length - 1]);

    return results;
}

export function colorToCss(color: any, opacity?: boolean | number) {
    if (isArray(color)) {
        var a: number[] = color;

        if (opacity !== false && (a.length > 3 || isNumber(opacity))) {
            if (opacity === true || !isNumber(opacity)) {
                return `rgba(${a[0]}, ${a[1]}, ${a[2]}, ${a[3]})`;
            } else {
                return `rgba(${a[0]}, ${a[1]}, ${a[2]}, ${opacity.toString()})`;
            }
        }

        return `rgb(${a[0]}, ${a[1]}, ${a[2]})`;
    }

    return color;
}

/**
 * Source: http://stackoverflow.com/questions/946544/good-text-foreground-color-for-a-given-background-color
 *
 * @param color is expected to be an array of 3 numbers [r, g, b]
 */
export function colorToGray(rgb: RGBColor): number {
    var g = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114;
    return Math.round(g);
}

/**
 * Source: http://stackoverflow.com/questions/946544/good-text-foreground-color-for-a-given-background-color
 *
 * @param color is expected to be an array of 3 numbers [r, g, b]
 * @returns true if white should be used as a foreground, false otherwise (black)
 */
export function getWhiteBlackForegroundFromBackgroundColor(rgb: RGBColor, threshold = 185): boolean {
    var g = colorToGray(rgb);
    return g <= threshold;
}

export function changeLuminosity(rgb: RGBColor, factor: number): RGBColor {
    var hsl = rgbToHsl(rgb[0], rgb[1], rgb[2]);

    hsl[2] *= factor;

    var alpha = rgb.length > 3 ? rgb[3] : null;

    rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);

    if (isNumber(alpha)) {
        rgb.push(alpha);
    }

    return rgb;
}

export function darken(color: RGBColor, ratio: number) {
    return [
        Math.max(Math.round(color[0] * ratio), 0),
        Math.max(Math.round(color[1] * ratio), 0),
        Math.max(Math.round(color[2] * ratio), 0),
    ];
}

export function lighten(color: RGBColor, ratio: number) {
    return [
        Math.min(Math.round(color[0] * (1 + ratio)), 255),
        Math.min(Math.round(color[1] * (1 + ratio)), 255),
        Math.min(Math.round(color[2] * (1 + ratio)), 255),
    ];
}

export function setOpacity(color: RGBColor, opacity: number): RGBAColor {
    // Note: "color" can be read-only if it has been pulled from an RTK store,
    // so must be cloned before being updated
    color = color ? [...color] : []
    color[3] = opacity
    return color
}
